import React, { useCallback, useState, useMemo } from "react";
import styled, { withTheme } from "styled-components";
import {
  DateFormat,
  studiesIds,
  studyStatusesBeyondXLH402,
  studyStatusesFAOD402,
  threadIdRegex,
  TimeZone,
} from "../../../../modules/constants";
import AlertUGNX from "../../../ToastsAndAlerts/AlertUGNX";
import { AlertType } from "../../../ToastsAndAlerts/constants";
import CustomDropdown from "../../../ui-library/CustomDropdown/CustomDropdown";
import LabelledInput from "../../../ui-library/LabelledInput/LabelledInput";
import PrimaryButton from "../../../ui-library/Buttons/PrimaryButton/PrimaryButton";
import { isNumeric } from "../../../../modules/utils";
import Checkbox from "../../../ui-library/Checkbox/Checkbox";
import DateSelect from "../../../ui-library/DateAndTime/Date/DateSelect";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { DEFAULT_LOCALE, getLanguages } from "../../../../i18n/helpers";

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledContainerHorizontal = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  @media (max-width: 576px) {
    padding-top: 2rem;
    margin-top: auto;
    .btn {
      width: 100%;
    }
  }
`;

const StyledLabel = styled.label`
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
`;

const StyledError = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: ${(props) => props.theme?.cardinal};
  text-transform: uppercase;
`;

const DatePickerContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  &.month-select {
    .react-datepicker__month-container > .react-datepicker__header,
    .react-datepicker__navigation {
      display: none;
    }
    .react-datepicker__month {
      margin: 1rem 0.4rem;
    }
  }
`;

const minDate = 1900;
const presentYear = new Date().getFullYear();

const numberRegex = "[0-9]{3}";
const participantIdRegex = new RegExp(`^${numberRegex}(-${numberRegex})$`);
const participantIdInputRegex = new RegExp("^([0-9]{1,3}-)(?:[0-9]{1,3})?$");
const yearRegex = new RegExp("^[0-9]{1,4}$");

const maxOriginalConsentDate = new Date(
  new Date().toLocaleDateString(DEFAULT_LOCALE, {
    timeZone: TimeZone.PST,
  })
);

const AddNewParticipant = (props) => {
  const { onSubmit, studyId, siteId } = props;

  const { t } = useTranslation();

  const [participantId, setParticipantId] = useState("");
  const [yearOfBirth, setYearOfBirth] = useState("");
  const [threadId, setThreadId] = useState("");
  const [isConsentSigned, setIsConsentSigned] = useState(false);
  const [isIdVerified, setIsIdVerified] = useState(false);
  const [isDiagnosisConfirmed, setIsDiagnosisConfirmed] = useState(false);
  const [showErrorDate, setShowErrorDate] = useState(false);
  const [isValidDate, setIsValidDate] = useState(true);
  const [dateOfEnrollment, setDateOfEnrollment] = useState(null);
  const [language, setLanguage] = useState("en-US");
  const [errors, setErrors] = useState({});

  const handleParticipantIdOnChange = (value) => {
    const participantIDFirstNumbersRegex = new RegExp(`^${numberRegex}$`);

    if (isNumeric(value)) {
      //avoid adding "-" if the user is deleting chars
      if (
        participantId.length < value.length &&
        new RegExp(participantIDFirstNumbersRegex).test(value)
      ) {
        setParticipantId(`${value}-`);
      } else if (value.length >= 4 && !value.includes("-")) {
        setParticipantId(
          `${value.substring(0, 3)}-${value.substring(3, value.length)}`
        );
      } else {
        setParticipantId(value);
      }
    } else if (value === "" || participantIdInputRegex.test(value)) {
      setParticipantId(value);
    }
  };

  const handleYearOfBirthDateChange = (value) => {
    if (value === "" || (value && yearRegex.test(value))) {
      setYearOfBirth(value);
    }
  };

  const handleDateOfEnrollmentChange = (value) => {
    if (value) {
      validateDateOfEnrollment(value);
      setDateOfEnrollment(value);
    }
  };

  const handleConsentSignedOnchange = (value) => {
    validateConsentSigned(!value);
    setIsConsentSigned(!value);
  };

  const handleVerifiedIDOnChange = (value) => {
    setIsIdVerified(!value);
  };

  const handleDiagnosisConfirmedChange = (value) => {
    setIsDiagnosisConfirmed(!value);
  };

  const validadeYearBirth = () => {
    setErrors({
      ...errors,
      yearOfBirth: isYearBirthValid()
        ? undefined
        : t("errorText.yearOfBirth.invalid", {
            minYear: minDate,
            maxYear: presentYear,
          }),
    });
  };

  const errorHandling = {
    showErrorDate: showErrorDate,
    setShowErrorDate: setShowErrorDate,
    isValidDate: isValidDate,
    setIsValidDate: setIsValidDate,
  };

  const availableLanguages = useMemo(
    () => getLanguages().filter((language) => language.isAvailable),
    []
  );

  const validadeParticipantId = () => {
    if (participantId === "") {
      setErrors({
        ...errors,
        participantId: isParticipantIdValid()
          ? undefined
          : t("errorText.required"),
      });
    } else {
      setErrors({
        ...errors,
        participantId: isParticipantIdValid()
          ? undefined
          : t("errorText.participantId.invalid"),
      });
    }
  };

  const validadeThreadId = (value) => {
    setErrors({
      ...errors,
      threadId: isThreadIdValid(value)
        ? undefined
        : t("errorText.threadId.invalid"),
    });
  };

  const validateConsentSigned = (value) => {
    setErrors({
      ...errors,
      isConsentSigned: isConsentSignedValid(value)
        ? undefined
        : t("errorText.required"),
    });
  };

  const validateDateOfEnrollment = (value) => {
    setErrors({
      ...errors,
      dateOfEnrollment: isDateOfEnrollmentValid(value)
        ? undefined
        : t("errorText.required"),
    });
  };

  const isYearBirthValid = useCallback(() => {
    if (yearOfBirth && (yearOfBirth < minDate || yearOfBirth > presentYear)) {
      return false;
    }
    return true;
  }, [yearOfBirth]);

  const isParticipantIdValid = useCallback(() => {
    if (!participantIdRegex.test(participantId)) {
      return false;
    }
    return true;
  }, [participantId]);

  const isThreadIdValid = useCallback(() => {
    if (threadId && !threadIdRegex.test(threadId)) {
      return false;
    }
    return true;
  }, [threadId]);

  const isConsentSignedValid = useCallback((value) => {
    return value;
  }, []);

  const isDateOfEnrollmentValid = useCallback((value) => {
    return !!value;
  }, []);

  const validadeBeforeSubmit = (event) => {
    event.preventDefault();
    checkErrors();

    if (isParentFormValid()) {
      submitForm();
    }
  };

  const checkErrors = useCallback(() => {
    setErrors((prevState) => ({
      ...prevState,
      yearOfBirth: isYearBirthValid()
        ? undefined
        : t("errorText.yearOfBirth.invalid", {
            minYear: minDate,
            maxYear: presentYear,
          }),
      participantId:
        participantId === ""
          ? t("errorText.required")
          : isParticipantIdValid()
          ? undefined
          : t("errorText.participantId.invalid"),
      isConsentSigned: isConsentSignedValid(isConsentSigned)
        ? undefined
        : t("errorText.required"),
      dateOfEnrollment: isDateOfEnrollmentValid(dateOfEnrollment)
        ? undefined
        : t("errorText.required"),
      threadId: isThreadIdValid(threadId)
        ? undefined
        : t("errorText.threadId.invalid"),
    }));
  }, [
    isYearBirthValid,
    t,
    participantId,
    isParticipantIdValid,
    isConsentSignedValid,
    isConsentSigned,
    isDateOfEnrollmentValid,
    dateOfEnrollment,
    isThreadIdValid,
    threadId,
  ]);

  const isParentFormValid = () =>
    isYearBirthValid() &&
    isParticipantIdValid() &&
    isConsentSignedValid(isConsentSigned) &&
    isDateOfEnrollmentValid(dateOfEnrollment) &&
    isThreadIdValid(threadId);

  const activeStudyStatus = useMemo(() => {
    switch (studyId?.toLocaleLowerCase()) {
      case studiesIds.lcfaod402.toLocaleLowerCase():
        return studyStatusesFAOD402.ACTIVE;
      case studiesIds.beyondxlh402.toLocaleLowerCase():
        return studyStatusesBeyondXLH402.ACTIVE;
      default:
        break;
    }
  }, [studyId]);

  const submitForm = useCallback(() => {
    const formData = {
      studyId,
      siteId,
      ugnxId: participantId,
      yearOfBirth: parseInt(yearOfBirth),
      signedConsent: isConsentSigned,
      idVerified: isIdVerified,
      diagnosisConfirmed: isDiagnosisConfirmed,
      language,
      studyStatus: activeStudyStatus.id,
      dateOfEnrollment: moment(dateOfEnrollment).format(
        DateFormat.EXTENDED_DATETIME
      ),
      threadId,
    };

    onSubmit(formData);
  }, [
    studyId,
    siteId,
    participantId,
    yearOfBirth,
    isConsentSigned,
    isIdVerified,
    isDiagnosisConfirmed,
    language,
    dateOfEnrollment,
    threadId,
    onSubmit,
  ]);

  return (
    <FormContainer data-testid="addnewparticipantId">
      <LabelledInput
        tabIndex={1}
        data-testid="participantId-input"
        className="mt-4"
        title={t("appText.participantID")}
        value={participantId}
        type="text"
        placeholder="XXX-XXX"
        hasError={!!errors?.participantId}
        errorMessage={errors?.participantId}
        onChange={(e) => handleParticipantIdOnChange(e.target.value)}
        onBlur={() => validadeParticipantId()}
      />

      <LabelledInput
        tabIndex={2}
        data-testid="yearOfBirth-input"
        className="mt-4"
        title={t("appText.yearOfBirth")}
        min={minDate}
        max={presentYear}
        value={yearOfBirth}
        type="text"
        placeholder="YYYY"
        pattern="[0-9]*"
        hasError={!!errors?.yearOfBirth}
        errorMessage={errors?.yearOfBirth}
        onChange={(e) => {
          e.target.validity.valid &&
            handleYearOfBirthDateChange(e.target.value);
        }}
        onBlur={() => validadeYearBirth()}
      />

      <div>
        <DatePickerContainer className="mt-4">
          <StyledLabel className="mb-0 mr-2">
            {t("appText.addParticipant.originalConsent")}
          </StyledLabel>
          <DateSelect
            eventDate={dateOfEnrollment}
            setEventDate={handleDateOfEnrollmentChange}
            displayTitle={false}
            errorHandling={errorHandling}
            maxDate={maxOriginalConsentDate}
            showMonthDropdown
            showYearDropdown
          />
        </DatePickerContainer>
        <StyledError>
          {errors?.dateOfEnrollment && <span>{errors?.dateOfEnrollment}</span>}
        </StyledError>
      </div>

      {studiesIds.beyondxlh402 === studyId && (
        <LabelledInput
          tabIndex={4}
          data-testid="threadId-input"
          className="mt-4"
          title={t("appText.addParticipant.threadId")}
          value={threadId}
          hasError={!!errors?.threadId}
          errorMessage={errors?.threadId}
          type="text"
          onChange={(e) =>
            e.target.validity.valid && setThreadId(e.target.value)
          }
          onBlur={() => validadeThreadId()}
          pattern=".{0,36}"
        />
      )}

      <StyledContainerHorizontal className="mt-4">
        <StyledLabel className="mb-0 mr-2">
          {t("appText.addParticipant.studyStatus")}
        </StyledLabel>
        <CustomDropdown
          placeholder={activeStudyStatus.value}
          disabled
          width={160}
        />
      </StyledContainerHorizontal>

      <span className="mt-4">
        <Checkbox
          tabIndex={5}
          data-testid="consentSigned-checkbox"
          checked={isConsentSigned}
          onClick={(e) => handleConsentSignedOnchange(isConsentSigned)}
          icon={true}
          square={true}
        />
        <StyledLabel
          className="ml-2"
          onClick={(e) => handleConsentSignedOnchange(isConsentSigned)}
        >
          {t("appText.addParticipant.informedConsentSigned")}
        </StyledLabel>
      </span>
      <StyledError>
        {errors?.isConsentSigned && <span>{errors?.isConsentSigned}</span>}
      </StyledError>

      <span className="mt-2">
        <Checkbox
          tabIndex={6}
          data-testid="verifiedID-checkbox"
          checked={isIdVerified}
          onClick={(e) => handleVerifiedIDOnChange(isIdVerified)}
          icon={true}
          square={true}
        />
        <StyledLabel
          className="ml-2"
          onClick={(e) => handleVerifiedIDOnChange(isIdVerified)}
        >
          {t("appText.addParticipant.idVerified")}
        </StyledLabel>
      </span>

      <span className="mt-2">
        <Checkbox
          tabIndex={7}
          data-testid="diagnosis-confirmed-checkbox"
          checked={isDiagnosisConfirmed}
          onClick={(e) => handleDiagnosisConfirmedChange(isDiagnosisConfirmed)}
          icon={true}
          square={true}
        />
        <StyledLabel
          className="ml-2"
          onClick={(e) => handleDiagnosisConfirmedChange(isDiagnosisConfirmed)}
        >
          {t("appText.addParticipant.diagnosisConfirmed")}
        </StyledLabel>
      </span>

      {studyId?.toLocaleLowerCase() === studiesIds.lcfaod402 && (
        <StyledContainerHorizontal className="mt-4">
          <StyledLabel>Select language:</StyledLabel>
          <CustomDropdown
            tabIndexProp={7}
            placeholder={
              getLanguages().find((language) => language.code === "en-US")
                ?.value ?? "Select"
            }
            values={availableLanguages}
            width={145}
            onChange={(lang) => setLanguage(lang.code)}
          />
        </StyledContainerHorizontal>
      )}

      <div className="mt-4">
        <AlertUGNX type={AlertType.WARNING}>
          {t("appText.addParticipant.confirmInfo")}
        </AlertUGNX>
      </div>

      <StyledFooter>
        <PrimaryButton
          tabIndex={9}
          data-testid="submitparticipant"
          onClick={(e) => validadeBeforeSubmit(e)}
        >
          {t("appText.addParticipant.submit")}
        </PrimaryButton>
      </StyledFooter>
    </FormContainer>
  );
};

export default withTheme(AddNewParticipant);
